// ─────────────────────────────────────────────────────────────
// LoopBuilderInfoPanel — Sunday Triggers Loop Builder editor.
// Sibling to SongInfoPanel — same tab shell, but exclusively
// for projects whose meta.st_playerType === "loopBuilder".
//
// Tabs: Info | Catalog | Categories | Time Signatures
// (Catalog, Categories, and Time Signatures are stubbed for v1
//  and will graduate to full management views as needed.)
// ─────────────────────────────────────────────────────────────

import css from "../st-panel/STPanelOverlay.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {Lifecycle, Terminator} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {STApi} from "@/jerry/st-panel/STApi"
import {LoopBuilderInfoView} from "./views/LoopBuilderInfoView"
import {LoopBuilderCatalogView} from "./views/LoopBuilderCatalogView"
import {LoopBuilderCategoriesView} from "./views/LoopBuilderCategoriesView"

const className = Html.adoptStyleSheet(css, "LoopBuilderInfoPanel")

type Tab = "info" | "catalog" | "categories"

const TAB_LABELS: Record<Tab, string> = {
    info:       "Loop Info",
    catalog:    "Catalog",
    categories: "Categories",
}

type Construct = {lifecycle: Lifecycle; service: StudioService}

export const LoopBuilderInfoPanel = ({lifecycle, service}: Construct) => {
    let activeTab: Tab = "info"
    const viewLifecycle: Terminator = lifecycle.own(new Terminator())

    const content: HTMLElement = <div className="st-content"/>
    const navEl: HTMLElement   = <div className="st-nav"/>

    const renderContent = () => {
        viewLifecycle.terminate()
        Html.empty(content)
        const view = (() => {
            switch (activeTab) {
                case "info":       return LoopBuilderInfoView({lifecycle: viewLifecycle, service})
                case "catalog":    return LoopBuilderCatalogView({lifecycle: viewLifecycle, service})
                case "categories": return LoopBuilderCategoriesView({lifecycle: viewLifecycle})
            }
        })()
        content.appendChild(view as Node)
    }

    const navBtns: Partial<Record<Tab, HTMLElement>> = {}
    const tabs: Tab[] = ["info", "catalog", "categories"]

    const setTab = (tab: Tab) => {
        activeTab = tab
        for (const [key, btn] of Object.entries(navBtns)) {
            btn?.classList.toggle("active", key === tab)
        }
        STApi.seedFromD1().then(() => renderContent())
    }

    for (const tab of tabs) {
        const btn: HTMLElement = (
            <div className={`st-nav-btn${tab === activeTab ? " active" : ""}`}>
                {TAB_LABELS[tab]}
            </div>
        )
        btn.addEventListener("click", () => setTab(tab))
        navBtns[tab] = btn
        navEl.appendChild(btn)
    }

    STApi.seedFromD1().then(() => renderContent())

    // Re-render when the project's player type changes (e.g. the user
    // pressed "Switch this project to Loop Builder" in Song Info).
    const onPlayerTypeChanged = () => renderContent()
    document.addEventListener("st:playertype", onPlayerTypeChanged)
    lifecycle.own({terminate: () => document.removeEventListener("st:playertype", onPlayerTypeChanged)})

    return (
        <div className={className}>
            {navEl}
            {content}
        </div>
    )
}
